import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppShell } from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EditorPage from "@/pages/EditorPage";
import PrintPage from "@/pages/PrintPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import SettingsPage from "@/pages/SettingsPage";
import "@/App.css";

const RequireAuth = ({ children }) => {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const RequireAdmin = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />

    <Route
      path="/"
      element={
        <RequireAuth>
          <AppShell>
            <DashboardPage />
          </AppShell>
        </RequireAuth>
      }
    />
    <Route
      path="/new"
      element={
        <RequireAuth>
          <AppShell>
            <EditorPage mode="new" />
          </AppShell>
        </RequireAuth>
      }
    />
    <Route
      path="/edit/:id"
      element={
        <RequireAuth>
          <AppShell>
            <EditorPage mode="edit" />
          </AppShell>
        </RequireAuth>
      }
    />
    <Route
      path="/print/:id"
      element={
        <RequireAuth>
          <PrintPage />
        </RequireAuth>
      }
    />
    <Route
      path="/settings"
      element={
        <RequireAuth>
          <AppShell>
            <SettingsPage />
          </AppShell>
        </RequireAuth>
      }
    />
    <Route
      path="/admin/users"
      element={
        <RequireAdmin>
          <AppShell>
            <AdminUsersPage />
          </AppShell>
        </RequireAdmin>
      }
    />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
